import type { BundledLanguage, Highlighter, ThemedToken } from "shiki";

import type { LanguageId } from "@/lib/code/language";

const DEFAULT_THEME = "dark-plus";

let highlighterPromise: Promise<Highlighter> | null = null;
const languageLoaders = new Map<LanguageId, Promise<void>>();
type ShikiLanguageInput = BundledLanguage;

async function createHighlighter(): Promise<Highlighter> {
  if (!highlighterPromise) {
    highlighterPromise = import("shiki").then(({ getHighlighter }) =>
      getHighlighter({ themes: [DEFAULT_THEME], langs: [] }),
    );
  }

  return highlighterPromise;
}

async function ensureLanguageLoaded(highlighter: Highlighter, lang: LanguageId) {
  if (highlighter.getLoadedLanguages().includes(lang)) {
    return;
  }

  const existingLoader = languageLoaders.get(lang);
  if (existingLoader) {
    await existingLoader;
    return;
  }

  const shikiLanguage = lang as ShikiLanguageInput;
  const loadPromise = highlighter
    .loadLanguage(shikiLanguage)
    .catch((error) => {
      languageLoaders.delete(lang);
      throw error;
    })
    .then(() => {
      languageLoaders.delete(lang);
    });

  languageLoaders.set(lang, loadPromise);
  await loadPromise;
}

export type TokenLine = ThemedToken[];

export async function highlightCodeToTokens(code: string, lang?: LanguageId): Promise<TokenLine[] | null> {
  if (!lang) {
    return null;
  }

  const highlighter = await createHighlighter();
  await ensureLanguageLoaded(highlighter, lang);
  const result = await highlighter.codeToTokens(code, {
    lang: lang as ShikiLanguageInput,
    theme: DEFAULT_THEME,
  });
  return result.tokens;
}
